import React, { useState } from "react";
import {
  Box,
  Input,
  CloseButton,
  List,
  ListItem,
  HStack,
  Spinner,
} from "@chakra-ui/react";
import { AiOutlineSearch } from "react-icons/ai";
import { AxiosInstance } from "axios";
import { isNumeric } from "./utils/placeEnquiryUtil";

interface GaraazSearchProps {
  placeholder: string;
  onSelect: (value: any) => void;
  onClear?: () => void;
  onChange: (value: string) => void;
  axios: AxiosInstance;
  baseSearchUrl: string;
  hideSearchIcon?: boolean;
  idField: string;
  valueField: string;
  acceptMobileNumber?: boolean;
  value: string;
  isDisabled?: boolean;
  /** Format display in dropdown (e.g. "Name-(phone)") */
  formatDisplay?: (data: any) => string;
  addCountryCodeToSearch?: boolean;
}

const GaraazSearch: React.FC<GaraazSearchProps> = (props) => {
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [typingTimeout, setTypingTimeout] =
    useState<ReturnType<typeof setTimeout> | null>(null);

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (typingTimeout) clearTimeout(typingTimeout);
    if (props.acceptMobileNumber && isNumeric(value) && value.length > 10)
      return;
    props.onChange(value);
    if (value.trim() === "") {
      setResults([]);
      return;
    }
    setTypingTimeout(
      setTimeout(() => {
        fetchData(value);
      }, 400),
    );
  };

  const fetchData = async (text: string) => {
    setLoading(true);
    const searchParam =
      props.addCountryCodeToSearch && props.acceptMobileNumber && isNumeric(text) ? `91${text}` : text;
    const url = `${props.baseSearchUrl}${encodeURIComponent(searchParam)}`;
    const { data } = await props.axios.get(url);
    setResults(
      Array.isArray(data)
        ? data.map((item: any) => ({
            id: item[props.idField],
            data: item,
            value: props.formatDisplay
              ? props.formatDisplay(item)
              : item[props.valueField],
          }))
        : [],
    );
    setLoading(false);
  };

  const handleClear = () => {
    setResults([]);
    props.onClear?.();
  };

  return (
    <Box
      border="1px"
      w="100%"
      borderColor="gray.300"
      borderRadius="7px"
      position="relative"
    >
      <HStack alignItems="center" pr={2}>
        {!props.hideSearchIcon && <AiOutlineSearch />}
        <Input
          placeholder={props.placeholder}
          value={props.value}
          onChange={handleInputChange}
          size="md"
          border="none"
          _placeholder={{ fontSize: "sm" }}
          isDisabled={props.isDisabled}
        />
        {(loading && <Spinner size="sm" />) ||
          (!!props.value?.length && !props.isDisabled && (
            <CloseButton onClick={handleClear} size="md" />
          ))}
      </HStack>
      {results.length > 0 && (
        <List
          bg="white"
          shadow="md"
          mt="1"
          p="2"
          rounded="md"
          position="absolute"
          top={10}
          left={0}
          zIndex="dropdown"
          width="100%"
        >
          {results.map((r) => (
            <ListItem
              key={r.id}
              px="2"
              py="1"
              _hover={{ bg: "gray.100" }}
              onClick={() => {
                props.onSelect(r.data);
                props.onChange(r.value);
                setResults([]);
              }}
            >
              {r.value}
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};

export default GaraazSearch;
